/**
 * Tab storage for the campaign the visitor landed with.
 * A later page that has no UTMs must not wipe these, and must not be
 * recorded as organic when the form opens.
 */

import { isAdClickId } from 'lib/analytics/forwardedParams';

export const LANDING_PARAMS_KEY = 'uh_landing_params';

export const LANDING_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'affiliate',
  'affiliateCode',
  'referral',
  'discount',
  'sscid',
  'irclickid',
  'subId1',
  'subId2',
  'subId3',
  'rvo_aff',
  'click_id',
  'gclid',
  'fbclid',
  'gbraid',
  'wbraid',
  'gclsrc',
  'gad_source',
  'gad_campaignid',
  'ttclid',
  'fbadid',
  'tw_source',
  'tw_adid',
  'tw_campaign',
  'rdt_cid',
  'msclkid',
] as const;

export type LandingParamKey = (typeof LANDING_PARAM_KEYS)[number];
export type LandingParams = Partial<Record<LandingParamKey, string>>;

/** Typeform hidden-field click ids. */
const CLICK_IDS = ['gclid', 'fbclid'] as const;
/** Held in memory, not session storage, until ads consent: every ad or affiliate click id. */
const CONSENT_HELD_KEYS = LANDING_PARAM_KEYS.filter(isAdClickId);

/** Click ids and Impact ids for this document. Not written to the browser until ads consent. */
let heldMemory: LandingParams = {};
/** Click ids taken out of the address bar that are not landing params. */
let strippedIds: Record<string, string> = {};

function storage(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

export function readLandingParams(): LandingParams {
  const store = storage();
  if (!store) return {};
  try {
    const raw = store.getItem(LANDING_PARAMS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: LandingParams = {};
    for (const key of LANDING_PARAM_KEYS) {
      const value = parsed[key];
      if (typeof value === 'string' && value) out[key] = value;
    }
    return out;
  } catch {
    return {};
  }
}

function isConsentHeld(key: LandingParamKey): boolean {
  return isAdClickId(key);
}

/** Keep UTMs and affiliate keys in the tab. Click ids and Impact ids stay in memory until persistLandingClickIds. */
export function rememberLandingParams(search: string): LandingParams {
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search,
  );
  const stored = readLandingParams();
  for (const key of CONSENT_HELD_KEYS) {
    if (!heldMemory[key] && stored[key]) heldMemory[key] = stored[key];
  }
  const next: LandingParams = { ...stored };
  let changed = false;
  for (const key of LANDING_PARAM_KEYS) {
    const value = params.get(key);
    if (!value) continue;
    if (isConsentHeld(key)) {
      if (heldMemory[key] !== value) heldMemory[key] = value;
      continue;
    }
    if (next[key] === value) continue;
    next[key] = value;
    changed = true;
  }
  if (changed) writeLandingParams(next);
  return { ...next, ...heldMemory };
}

/** Write the in-memory click ids after ads consent. A reload can then restore them. */
export function persistLandingClickIds(): void {
  const next: LandingParams = { ...readLandingParams() };
  let changed = false;
  for (const key of CONSENT_HELD_KEYS) {
    const value = heldMemory[key];
    if (!value || next[key] === value) continue;
    next[key] = value;
    changed = true;
  }
  if (changed) writeLandingParams(next);
}

function writeLandingParams(next: LandingParams): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(LANDING_PARAMS_KEY, JSON.stringify(next));
  } catch {
    // Session storage can be blocked. The URL still wins for this page.
  }
}

/**
 * Click ids for the Typeform. Empty until ads consent, so the form does not
 * receive an ad identifier before a choice.
 */
export function landingClickIds(
  search: string,
  adsAllowed: boolean,
): Record<string, string> {
  rememberLandingParams(search);
  if (!adsAllowed) return {};
  persistLandingClickIds();
  const out: Record<string, string> = {};
  for (const key of CLICK_IDS) {
    const value = heldMemory[key];
    if (value) out[key] = value;
  }
  return out;
}

export function resetLandingParamsForTests(): void {
  heldMemory = {};
  strippedIds = {};
  storage()?.removeItem(LANDING_PARAMS_KEY);
}

/**
 * Ad click ids stay out of the address bar while ads are not allowed, so
 * analytics tools that read location.href (Mixpanel, Clarity, GA4) never see
 * them. They are already held in memory and go back into the URL on an ads
 * yes, before the ad tags load and read them.
 */
export function syncClickIdsInUrl(adsAllowed: boolean): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  let changed = false;
  if (adsAllowed) {
    for (const [key, value] of Object.entries({ ...strippedIds, ...heldMemory })) {
      if (!value || url.searchParams.has(key)) continue;
      url.searchParams.set(key, value);
      changed = true;
    }
  } else {
    for (const [key, value] of [...url.searchParams.entries()]) {
      if (!isAdClickId(key)) continue;
      // Ids outside LANDING_PARAM_KEYS (twclid, srsltid, ...) are kept here for the restore.
      strippedIds[key] = value;
      url.searchParams.delete(key);
      changed = true;
    }
  }
  if (changed) window.history.replaceState(window.history.state, '', url);
}

/** Drop click ids held for a later ads yes. UTMs in session storage stay. */
export function forgetLandingClickIds(): void {
  heldMemory = {};
  strippedIds = {};
  const stored = readLandingParams();
  let changed = false;
  for (const key of CONSENT_HELD_KEYS) {
    if (!stored[key]) continue;
    delete stored[key];
    changed = true;
  }
  if (changed) writeLandingParams(stored);
}
